// package imports
import { Session } from 'next-auth';
import { signOut } from 'next-auth/react';

// local imports
import {
  clearSessionStore,
  setSigningOutState,
} from '../../../services/storageUtils';
import { clientSideLogout } from '../../../services/authClientUtils';
import {
  MegaMenuCTACard,
  MegaMenuSection,
  NavigationItem,
} from './SiteHeaderTypes';
import {
  hasRealityCoachAccess,
  isCustomer,
} from '../../../services/subscriptionUtils';
import { isBoysTownAdmin } from '../../../services/roleUtils';
import { canAccessObservations } from '../../agility-pageModules/observation/utils/ObservationUtils';
import { isFeatureSwitchOnClientAsync } from '../../../services/featureSwitchClientUtils';

export const doSignOut = async (session: Session) => {
  clearSessionStore();
  setSigningOutState();
  if (session && session.logout_url) {
    const parsedUrl = new URL(session.logout_url);
    const allowedHosts = ['b2clogin.com'];
    if (allowedHosts.find((h) => parsedUrl.host.indexOf(h) > -1)) {
      // force clearing the b2c signin session.
      await signOut({ redirect: false });
      //window.setTimeout(async () => {
      await clientSideLogout(session);
      //}, 100);
    } else {
      signOut({ callbackUrl: '/' });
    }
  } else {
    signOut({ callbackUrl: '/' });
  }
};

export async function filterNavigationLinks(
  links: NavigationItem[],
  session: Session | null,
  isMobile: boolean = false,
): Promise<NavigationItem[]> {
  if (!links || links.length === 0) {
    return [];
  }

  const filteredLinks = await Promise.all(
    links.map(async (link) => {
      if (!link.fields) {
        return null;
      }
      if (link.fields.featureSwitch && link.fields.featureSwitch?.length > 0) {
        const featureSwitchActive = await checkFeatureSwitches(
          link.fields.featureSwitch,
        );
        if (!featureSwitchActive) {
          return null;
        }
      }
      if (link.fields?.mobileDisplayOnly === 'true' && !isMobile) {
        return null;
      }
      if (link.fields.realityCoachPermissionRequired === 'true') {
        if (hasRealityCoachAccess(session)) {
          return link;
        }
        return null;
      }
      if (link.fields.subscriptionRequiredForDisplay === 'true') {
        if (isCustomer(session?.user)) {
          return link;
        }
        return null;
      }
      if (link.fields.notLoggedInUserOnlyDisplay === 'true') {
        if (session?.user) {
          return null;
        }
        return link;
      }
      if (link.fields.freeAccountUserOnlyDisplay === 'true') {
        if (session?.user && !isCustomer(session?.user)) {
          return link;
        }
        return null;
      }
      if (link.fields.adminOnlyDisplay === 'true') {
        if (isBoysTownAdmin(session)) {
          return link;
        }
        return null;
      }
      if (link.fields.observationPermissionRequired === 'true') {
        if (
          canAccessObservations(
            session,
            session?.user ? 'authenticated' : 'unauthenticated',
          ) &&
          !isBoysTownAdmin(session)
        ) {
          return link;
        }
        return null;
      }
      return link;
    }),
  );
  return filteredLinks.filter((link): link is NavigationItem => link !== null);
}

export const isMyAccountSection = (section: MegaMenuSection): boolean => {
  if (!section.fields || !section.fields.isAccountSection) {
    return false;
  }
  return (
    typeof section.fields.isAccountSection === 'string' &&
    section.fields.isAccountSection === 'true'
  );
};

const checkFeatureSwitches = async (
  featureSwitches: Array<{ fields: { textValue: string } }>,
): Promise<boolean> => {
  const results = await Promise.all(
    featureSwitches.map(async (feature) => {
      // If the feature switch has a textValue, check if it's enabled
      if (feature.fields && feature.fields.textValue) {
        return await isFeatureSwitchOnClientAsync(feature.fields.textValue);
      }
      return false;
    }),
  );
  return results.includes(true);
};

export const filterCtaCards = async (
  cards: MegaMenuCTACard[],
  session: Session | null,
): Promise<MegaMenuCTACard[]> => {
  const filteredCards = await Promise.all(
    cards.map(async (card) => {
      if (card.fields?.featureSwitch?.length > 0) {
        const featureSwitchActive = await checkFeatureSwitches(
          card.fields.featureSwitch,
        );
        if (!featureSwitchActive) {
          return null;
        }
      }
      if (card.fields?.freeAccountDisplayOnly === 'true') {
        if (session?.user && !isCustomer(session?.user)) {
          return card;
        }
        return null;
      } else if (card.fields?.isCustomerOnlyDisplay === 'true') {
        if (isCustomer(session?.user)) {
          return card;
        }
        return null;
      } else if (card.fields?.notLoggedInUserOnlyDisplay === 'true') {
        if (session?.user) {
          return null;
        }
        return card;
      }
      return card;
    }),
  );
  return filteredCards.filter((card): card is MegaMenuCTACard => card !== null);
};
